'use client';

import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import {
  Scroll,
  SummaryCard,
  Switch,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import { Grid, HStack, VStack } from '@terra/ui-styled-system';
import { Database01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AiApplicationAssetRow } from './AiApplicationAssetRow';

type ComponentDetectionMetrics = {
  hasFindings: number;
  hasSignals: number;
  noDetections: number;
};

export type AiApplicationComponentsAssetsViewProps = {
  componentsTabCount: number;
  filteredComponents: AiComponentDto[];
  signalCountsByComponentId: Record<string, number>;
  componentDetectionMetrics: ComponentDetectionMetrics;
  isSignalsUnavailable?: boolean;
  isSignalsLoading?: boolean;
  isFindingsUnavailable?: boolean;
  onSelectComponent: (componentId: string) => void;
};
export function AiApplicationComponentsAssetsView({
  componentsTabCount,
  filteredComponents,
  signalCountsByComponentId,
  componentDetectionMetrics,
  isSignalsUnavailable = false,
  isSignalsLoading = false,
  isFindingsUnavailable = false,
  onSelectComponent,
}: AiApplicationComponentsAssetsViewProps) {
  const t = useTranslations('aiApplications.assets');
  const tCommon = useTranslations('common');
  const [showComponentMetrics, setShowComponentMetrics] = useState(true);
  const hasFindingsValue = isFindingsUnavailable
    ? tCommon('na')
    : `${componentDetectionMetrics.hasFindings}`;
  const hasSignalsValue = isSignalsUnavailable
    ? tCommon('na')
    : `${componentDetectionMetrics.hasSignals}`;
  const noDetectionsValue =
    isFindingsUnavailable || isSignalsUnavailable
      ? tCommon('na')
      : `${componentDetectionMetrics.noDetections}`;

  if (filteredComponents.length === 0) {
    return (
      <VStack
        gap={3}
        alignItems="center"
        justifyContent="center"
        py={12}
        flex={1}
      >
        <Database01Icon size={24} opacity={0.3} />
        <Text fontSize="sm" color="fg.muted">
          {t('empty')}
        </Text>
      </VStack>
    );
  }

  return (
    <>
      <VStack
        alignItems="stretch"
        gap={4}
        px={8}
        py={4}
        boxShadow="xs"
        w="full"
      >
        <HStack justifyContent="space-between" alignItems="center" w="full">
          <Text fontSize="sm" color="fg.muted">
            {t('componentsWithCount', { count: componentsTabCount })}
          </Text>
          <Tooltip
            title={showComponentMetrics ? t('hideMetrics') : t('showMetrics')}
          >
            <HStack gap={2} alignItems="center">
              <Switch
                size="sm"
                checked={showComponentMetrics}
                onCheckedChange={(details) =>
                  setShowComponentMetrics(details.checked)
                }
              />
              <Text fontSize="sm">{t('metrics')}</Text>
            </HStack>
          </Tooltip>
        </HStack>
        {showComponentMetrics ? (
          <Grid
            gap={3}
            w="full"
            gridTemplateColumns={{
              base: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            }}
          >
            <SummaryCard
              title={t('hasFindings')}
              value={hasFindingsValue}
              tooltip={t('hasFindingsTooltip')}
            />
            <SummaryCard
              title={t('hasSignals')}
              value={hasSignalsValue}
              tooltip={t('hasSignalsTooltip')}
            />
            <SummaryCard
              title={t('noDetections')}
              value={noDetectionsValue}
              tooltip={t('noDetectionsTooltip')}
            />
          </Grid>
        ) : null}
      </VStack>

      <Grid
        w="full"
        px={8}
        py={2.5}
        borderBottomWidth="1px"
        borderColor="border.neutral.subtle"
        gridTemplateColumns="minmax(0, 1fr) 96px 96px 120px 120px"
        gap={4}
        alignItems="center"
      >
        <Text fontSize="sm" fontWeight="medium">
          {t('assetName')}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {t('signals')}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {t('role')}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {t('coverage')}
        </Text>
        <Text fontSize="sm" color="fg.muted" textAlign="right">
          {t('type')}
        </Text>
      </Grid>

      <Scroll w="full" h="full" showScrollbar orientation="vertical">
        <VStack alignItems="stretch" gap={0} w="full">
          {filteredComponents.map((component) => (
            <AiApplicationAssetRow
              key={component.id}
              component={component}
              signalsCount={signalCountsByComponentId[component.id] ?? 0}
              isSignalsUnavailable={isSignalsUnavailable || isSignalsLoading}
              onClick={() => onSelectComponent(component.id)}
            />
          ))}
        </VStack>
      </Scroll>
    </>
  );
}
